import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ConfigService } from '../../shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';

interface ViewTab {
  id: string;
  label: string;
  data?: any;
  apiUrl?: string;
  method?: string;
  title?: string;
  subtitle?: string;
  chartConfig?: any;
}

interface SeriesConfig {
  id: string;
  name: string;
  valueField: string;
  color?: string;
  barWidth?: number | string;
  barMaxWidth?: number | string;
  valuePrefix?: string;
  valueSuffix?: string;
  valueDecimals?: number;
  valueFormat?: string;
}

interface StatusCell {
  category: string;
  value: string;
  label: string;
  source?: any;
}

interface StatusMatrixRow {
  label: string;
  valueKey: string;
  cells: StatusCell[];
}

@Component({
  selector: 'app-grouped-bar-chart-with-status-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './grouped-bar-chart-with-status-table.component.html',
  styleUrl: './grouped-bar-chart-with-status-table.component.scss',
})
export class GroupedBarChartWithStatusTableComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() data!: any;
  @ViewChild('cardRoot', { static: false }) private cardRoot?: ElementRef<HTMLDivElement>;
  @ViewChild('plotScroll', { static: false }) private plotScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('chartHost', { static: false }) private chartHost?: ElementRef<HTMLDivElement>;

  activeViewIndex = 0;
  currentTitle = '';
  currentSubtitle = '';
  categoryCount = 0;
  viewTabs: ViewTab[] = [];
  statusMatrix: StatusMatrixRow[] = [];
  statusGridTemplate = '';
  categoryAreaWidth = 0;
  plotMinWidth = 0;
  labelColumnWidth = 132;
  gridRightWidth = 24;
  fitToWidth = false;
  pageStartIndex = 0;
  pageRowCount = 0;
  protected allCategories: string[] = [];
  protected allSeriesNames: string[] = [];
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  private myChart?: echarts.ECharts;
  private activeConfig: any = {};
  private lastRows: any[] = [];
  private lastRawRows: any[] = [];
  private lastSeriesConfigs: SeriesConfig[] = [];
  private dataSub?: Subscription;
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private resizeFrame = 0;
  private resizeHandler = () => this.scheduleResize();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);

  ngAfterViewInit(): void {
    this.refreshViewState();
    this.initChart();
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart || null,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
      onHighlightCategories: (categories) => this.highlightCategories(categories),
      onResetCategoryHighlight: () => this.resetCategoryHighlight(),
      onSelectLegendsByIndices: (indices) => this.selectLegendsByIndices(indices),
    });
    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.scheduleResize());
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.chartHost?.nativeElement) {
        return;
      }
      this.myChart?.dispose();
      this.myChart = echarts.init(this.chartHost.nativeElement, 'dvm');
      this.renderChart(this.lastRawRows);
    });

    window.addEventListener('resize', this.resizeHandler);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleResize());
      [this.cardRoot?.nativeElement, this.plotScroll?.nativeElement, this.chartHost?.nativeElement]
        .filter((element): element is HTMLDivElement => Boolean(element))
        .forEach((element) => this.resizeObserver?.observe(element));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.myChart) {
      this.activeViewIndex = 0;
      this.refreshViewState();
      this.wrapper.updateData(this.data);
      this.loadAndRenderChart();
    }
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.resizeObserver?.disconnect();
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
    }
    this.dataSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.resizeHandler);
    this.myChart?.dispose();
  }

  selectView(index: number): void {
    if (index === this.activeViewIndex) {
      return;
    }
    this.activeViewIndex = index;
    this.refreshViewState();
    this.loadAndRenderChart();
  }

  isCategorySelected(category: string): boolean {
    return this.wrapper.selectedCategories.some((item) => String(item) === String(category));
  }

  get maxVisibleGroups(): number {
    const configured = Number(this.getConfig().maxVisibleGroups ?? this.data?.maxVisibleGroups ?? 7);
    return Number.isFinite(configured) && configured > 0 ? configured : 7;
  }

  get hasPagedScroller(): boolean {
    return this.pageRowCount > this.maxVisibleGroups;
  }

  get pageEndIndex(): number {
    return Math.min(this.pageStartIndex + this.maxVisibleGroups, this.pageRowCount);
  }

  get pagerMax(): number {
    return Math.max(0, this.pageRowCount - this.maxVisibleGroups);
  }

  onPageStartChange(value: Event): void {
    const input = value.target as HTMLInputElement;
    this.pageStartIndex = this.clampPageStart(Number(input.value || 0), this.pageRowCount);
    this.renderChart(this.lastRawRows);
  }

  getStatusStyle(status: string): Record<string, string> {
    const normalized = this.normalizeStatus(status);
    const configured = this.getConfig().statusStyles || this.data?.statusStyles || {};
    const rawConfig = configured?.[status] || configured?.[normalized] || {};

    const defaults: Record<string, Record<string, string>> = {
      good: {
        background: '#dcfce7',
        color: '#16a34a',
        borderColor: 'transparent',
      },
      fair: {
        background: '#fef3c7',
        color: '#b45309',
        borderColor: 'transparent',
      },
      weak: {
        background: '#fee2e2',
        color: '#dc2626',
        borderColor: 'transparent',
      },
      neutral: {
        background: '#e5e7eb',
        color: '#4b5563',
        borderColor: 'transparent',
      },
    };

    const fallback = defaults[normalized] || defaults['neutral'];
    return {
      background: String(rawConfig['background'] || rawConfig['bg'] || fallback['background']),
      color: String(rawConfig['color'] || fallback['color']),
      borderColor: String(rawConfig['borderColor'] || fallback['borderColor']),
    };
  }

  private initChart(): void {
    if (!this.chartHost?.nativeElement || this.myChart) {
      return;
    }

    this.myChart = echarts.init(this.chartHost.nativeElement, 'dvm');
  }

  private loadAndRenderChart(filters?: any): void {
    this.initChart();
    this.dataSub?.unsubscribe();
    this.currentTitle = String(this.activeView().title || this.data?.title || 'Price Analysis');
    this.currentSubtitle = String(this.activeView().subtitle || this.data?.subtitle || '');

    const inlineData = this.activeView().data ?? this.data?.data;
    if (inlineData) {
      this.pageStartIndex = 0;
      this.renderChart(this.extractRows(inlineData));
      return;
    }

    const apiUrl = this.activeView().apiUrl || this.data?.apiUrl;
    if (!apiUrl) {
      this.pageStartIndex = 0;
      this.renderChart([]);
      return;
    }

    this.myChart?.showLoading();
    this.dataSub = this.configService
      .getConfig(apiUrl, this.activeWidgetData(), filters, this.activeView().method || this.data?.method)
      .subscribe({
        next: (response: any) => {
          this.pageStartIndex = 0;
          this.renderChart(this.extractRows(response));
        },
        error: () => {
          this.pageStartIndex = 0;
          this.renderChart([]);
        },
      });
  }

  private renderChart(rawRows: any[]): void {
    if (!this.myChart) {
      return;
    }

    this.lastRawRows = rawRows;
    const config = this.getConfig();
    const availableRows = this.limitRows(this.sortRows(rawRows, config));
    this.pageRowCount = availableRows.length;
    this.pageStartIndex = this.clampPageStart(this.pageStartIndex, this.pageRowCount);
    const rows = this.visibleRows(availableRows);
    this.lastRows = rows;
    const categories = this.buildCategories(rows);
    const seriesConfigs = this.resolveSeriesConfigs();
    const yAxisMax = this.resolveYAxisMax(rows, seriesConfigs, config);
    this.lastSeriesConfigs = seriesConfigs;
    this.categoryCount = categories.length;
    this.allCategories = categories;
    this.allSeriesNames = seriesConfigs.map((series) => series.name);
    this.labelColumnWidth = Number(config.gridLeft ?? config.labelColumnWidth ?? config.metricLabelWidth ?? 132);
    this.gridRightWidth = Number(config.gridRight ?? 24);
    this.fitToWidth = config.disableHorizontalScroll === true || config.fitToWidth === true;

    const categorySlotWidth = Number(config.categorySlotWidth ?? 148);
    this.categoryAreaWidth = Math.max(categories.length * categorySlotWidth, 1);
    this.plotMinWidth = this.fitToWidth
      ? 0
      : Math.max(0, this.labelColumnWidth + this.categoryAreaWidth + this.gridRightWidth);
    this.statusGridTemplate = this.fitToWidth
      ? `repeat(${Math.max(this.categoryCount, 1)}, minmax(0, 1fr))`
      : `repeat(${Math.max(this.categoryCount, 1)}, ${categorySlotWidth}px)`;
    this.statusMatrix = this.buildStatusMatrix(rows, categories);

    if (!rows.length || !categories.length || !seriesConfigs.length) {
      this.showNoData();
      return;
    }

    this.wrapper.setSourceData(
      rows,
      this.categoryKey(),
      ...(this.data?.filterKey ? [String(this.data.filterKey)] : []),
    );

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const option: echarts.EChartsOption = {
      color: seriesConfigs.map(
        (series, index) => this.wrapper.applyColorConsistency(series.name, series.color) || this.palette()[index % this.palette().length],
      ),
      legend: {
        show: config.showLegend !== false,
        type: config.legendType ?? 'plain',
        top: Number(config.legendTop ?? 6),
        left: config.legendLeft ?? 0,
        itemWidth: Number(config.legendItemWidth ?? 12),
        itemHeight: Number(config.legendItemHeight ?? 12),
        itemGap: Number(config.legendItemGap ?? 18),
        data: seriesConfigs.map((series) => series.name),
        textStyle: {
          color: theme.chart?.legendTextColor || '#374151',
          fontSize: Number(config.legendFontSize ?? 11),
          fontWeight: config.legendFontWeight ?? 500,
        },
      },
      tooltip: {
        show: config.tooltip?.show !== false,
        trigger: config.tooltipTrigger || 'axis',
        axisPointer: { type: config.axisPointerType || 'shadow' },
        confine: true,
        backgroundColor: theme.tooltip?.bg || '#ffffff',
        borderColor: theme.tooltip?.borderColor || '#d1d5db',
        textStyle: {
          color: theme.tooltip?.textColor || '#1f2937',
          fontSize: theme.tooltip?.fontSize || 12,
        },
        formatter: (params: any) => this.formatTooltip(params),
      },
      grid: {
        top: Number(config.gridTop ?? 74),
        right: this.gridRightWidth,
        bottom: Number(config.gridBottom ?? 44),
        left: this.labelColumnWidth,
        containLabel: config.containLabel === true,
      },
      xAxis: {
        type: 'category',
        data: categories,
        triggerEvent: true,
        axisTick: { show: false },
        axisLine: {
          lineStyle: {
            color: config.axisLineColor || theme.chart?.axisLineColor || '#d1d5db',
          },
        },
        axisLabel: {
          show: config.showXAxisLabels !== false,
          interval: 0,
          color: config.xAxisLabelColor || '#6b7280',
          fontSize: Number(config.xAxisFontSize ?? 11),
          fontWeight: config.xAxisFontWeight ?? 500,
          margin: Number(config.xAxisLabelMargin ?? 10),
        },
      },
      yAxis: {
        type: 'value',
        name: config.yAxisName || '',
        nameLocation: config.yAxisNameLocation || 'middle',
        nameGap: Number(config.yAxisNameGap ?? 54),
        min: config.yAxisMin,
        max: yAxisMax,
        interval: config.yAxisInterval,
        splitLine: {
          show: config.showSplitLine !== false,
          lineStyle: {
            color: config.splitLineColor || theme.chart?.splitLineColor || '#e5e7eb',
            type: config.splitLineType || 'solid',
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: config.yAxisLabelColor || '#9ca3af',
          fontSize: Number(config.yAxisFontSize ?? 11),
          formatter: (value: number) =>
            this.formatValue(
              value,
              Number(config.valueDecimals ?? 0),
              String(config.valuePrefix ?? ''),
              String(config.valueSuffix ?? ''),
              config.yAxisValueFormat ?? config.valueFormat,
            ),
        },
        nameTextStyle: {
          color: config.yAxisNameColor || '#6b7280',
          fontSize: Number(config.yAxisNameFontSize ?? 12),
          fontWeight: config.yAxisNameFontWeight ?? 700,
        },
      },
      series: seriesConfigs.map((series) => this.buildBarSeries(series, rows, categories, config)),
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    this.updateComparisonGraphics();
    this.myChart.hideLoading();
    this.wrapper.afterRender();
    this.scheduleResize();
  }

  private buildBarSeries(series: SeriesConfig, rows: any[], categories: string[], config: any): any {
    const resolvedColor = this.wrapper.applyColorConsistency(series.name, series.color) || series.color;
    return {
      name: series.name,
      type: 'bar',
      barWidth: series.barWidth ?? config.barWidth ?? 16,
      barMaxWidth: series.barMaxWidth ?? config.barMaxWidth ?? 24,
      barGap: config.barGap ?? '30%',
      itemStyle: {
        color: resolvedColor,
        borderRadius: config.barBorderRadius ?? [2, 2, 0, 0],
      },
      emphasis: { focus: 'series' },
      data: categories.map((_category, index) => {
        const row = rows[index];
        const value = Number(this.resolvePath(row, series.valueField));
        return {
          value: Number.isFinite(value) ? value : null,
          raw: row,
        };
      }),
      label: {
        show: config.showDataLabels === true,
        position: config.barLabelPosition || 'top',
        color: config.dataLabelColor || '#111827',
        fontSize: Number(config.dataLabelFontSize ?? 10),
        fontWeight: config.dataLabelFontWeight ?? 600,
        formatter: (params: any) =>
          this.formatValue(
            Number(params.value),
            Number(series.valueDecimals ?? config.valueDecimals ?? 0),
            String(series.valuePrefix ?? config.valuePrefix ?? ''),
            String(series.valueSuffix ?? config.valueSuffix ?? ''),
            series.valueFormat ?? config.dataLabelValueFormat ?? config.valueFormat,
          ),
      },
    };
  }

  private resolveYAxisMax(rows: any[], seriesConfigs: SeriesConfig[], config: any): number | undefined {
    const configuredMax = Number(config.yAxisMax);
    const comparison = config.comparison;
    const shouldAddHeadroom = comparison !== false && config.autoComparisonHeadroom !== false;

    if (!shouldAddHeadroom) {
      return Number.isFinite(configuredMax) ? configuredMax : undefined;
    }

    const maxValue = rows.reduce((max, row) => {
      const rowMax = seriesConfigs.reduce((seriesMax, series) => {
        const value = Number(this.resolvePath(row, series.valueField));
        return Number.isFinite(value) ? Math.max(seriesMax, value) : seriesMax;
      }, 0);
      return Math.max(max, rowMax);
    }, 0);

    if (!Number.isFinite(maxValue) || maxValue <= 0) {
      return Number.isFinite(configuredMax) ? configuredMax : undefined;
    }

    const configuredHeadroom = Number(comparison?.headroomRatio ?? config.comparisonHeadroomRatio ?? 0.2);
    const headroomRatio = Number.isFinite(configuredHeadroom) ? Math.max(0, configuredHeadroom) : 0.2;
    const paddedMax = maxValue * (1 + headroomRatio);
    return Math.max(Number.isFinite(configuredMax) ? configuredMax : 0, paddedMax);
  }

  private updateComparisonGraphics(): void {
    if (!this.myChart) {
      return;
    }

    const graphic = this.buildComparisonGraphics();
    this.myChart.setOption({ graphic }, false);
  }

  private buildComparisonGraphics(): any[] {
    if (!this.myChart || !this.lastRows.length || this.lastSeriesConfigs.length < 2) {
      return [];
    }

    const comparison = this.getConfig().comparison;
    if (comparison === false) {
      return [];
    }

    const baseIndex = this.resolveSeriesIndex(comparison?.baseSeries, 0);
    const compareIndex = this.resolveSeriesIndex(comparison?.compareSeries, 1);
    const baseSeries = this.lastSeriesConfigs[baseIndex];
    const compareSeries = this.lastSeriesConfigs[compareIndex];

    if (!baseSeries || !compareSeries || baseIndex === compareIndex) {
      return [];
    }

    const barSeriesCount = this.lastSeriesConfigs.length;
    const rawBarWidth = Number(this.getConfig().barWidth ?? baseSeries.barWidth ?? 16);
    const barWidth = Number.isFinite(rawBarWidth) ? rawBarWidth : 16;
    const gap = Number(this.getConfig().comparison?.seriesGap ?? this.getConfig().seriesGap ?? 6);
    const groupWidth = (barSeriesCount * barWidth) + (Math.max(0, barSeriesCount - 1) * gap);
    const lineColor = comparison?.lineColor || '#111111';
    const bubbleFill = comparison?.bubbleBackground || '#ffffff';
    const bubbleText = comparison?.textColor || '#111111';
    const bubbleBorder = comparison?.bubbleBorderColor || '#111111';
    const bubbleHeight = Number(comparison?.bubbleHeight ?? 22);
    const lineLift = Number(comparison?.lineLift ?? 26);
    const stemGap = Number(comparison?.stemGap ?? 8);
    const arrowSize = Number(comparison?.arrowSize ?? 5);
    const decimals = Number(comparison?.valueDecimals ?? 1);
    const prefix = String(comparison?.valuePrefix ?? '');
    const suffix = String(comparison?.valueSuffix ?? '%');
    const minBubbleTop = Number(comparison?.minBubbleTop ?? 4);
    const graphics: any[] = [];

    this.lastRows.forEach((row, index) => {
      const baseValue = Number(this.resolvePath(row, baseSeries.valueField));
      const compareValue = Number(this.resolvePath(row, compareSeries.valueField));
      if (!Number.isFinite(baseValue) || !Number.isFinite(compareValue) || baseValue === 0) {
        return;
      }

      const percent = comparison?.valueKey
        ? Number(this.resolvePath(row, comparison.valueKey))
        : ((compareValue - baseValue) / baseValue) * 100;
      if (!Number.isFinite(percent)) {
        return;
      }

      const centerX = Number(this.myChart?.convertToPixel({ xAxisIndex: 0 }, index));
      const basePoint = this.myChart?.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [index, baseValue]) as number[];
      const comparePoint = this.myChart?.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [index, compareValue]) as number[];
      if (!Number.isFinite(centerX) || !Array.isArray(basePoint) || !Array.isArray(comparePoint)) {
        return;
      }

      const baseX = centerX - (groupWidth / 2) + (baseIndex * (barWidth + gap)) + (barWidth / 2);
      const compareX = centerX - (groupWidth / 2) + (compareIndex * (barWidth + gap)) + (barWidth / 2);
      const topY = Math.min(basePoint[1], comparePoint[1]);
      const minLineY = minBubbleTop + bubbleHeight + 8;
      const maxLineY = topY - arrowSize - stemGap - 4;
      const lineY = Math.min(Math.max(topY - lineLift, minLineY), maxLineY);
      if (!Number.isFinite(lineY) || lineY >= topY - arrowSize) {
        return;
      }
      const leftX = Math.min(baseX, compareX);
      const rightX = Math.max(baseX, compareX);
      const bubbleTextValue = `${percent > 0 ? '+' : ''}${percent.toFixed(decimals)}${suffix}`;
      const label = comparison?.labelKey
        ? String(this.resolvePath(row, comparison.labelKey) ?? bubbleTextValue)
        : `${prefix}${bubbleTextValue}`;
      const bubbleWidth = Math.max(44, Math.round((label.length * 7.2) + 18));
      const chartWidth = this.myChart?.getWidth?.() ?? 0;
      const centerBubbleX = (leftX + rightX) / 2;
      const bubbleX = chartWidth > 0
        ? Math.max((bubbleWidth / 2) + 4, Math.min(centerBubbleX, chartWidth - (bubbleWidth / 2) - 4))
        : centerBubbleX;
      const lineBottomY = lineY + 2;

      graphics.push({
        type: 'group',
        silent: true,
        z: 12,
        children: [
          {
            type: 'line',
            shape: { x1: leftX, y1: lineY, x2: rightX, y2: lineY },
            style: { stroke: lineColor, lineWidth: 1.25 },
          },
          {
            type: 'line',
            shape: { x1: baseX, y1: lineBottomY, x2: baseX, y2: basePoint[1] - stemGap },
            style: { stroke: lineColor, lineWidth: 1.25 },
          },
          {
            type: 'line',
            shape: { x1: compareX, y1: lineBottomY, x2: compareX, y2: comparePoint[1] - stemGap },
            style: { stroke: lineColor, lineWidth: 1.25 },
          },
          {
            type: 'polygon',
            shape: {
              points: [
                [baseX, basePoint[1]],
                [baseX - arrowSize, basePoint[1] - arrowSize - 2],
                [baseX + arrowSize, basePoint[1] - arrowSize - 2],
              ],
            },
            style: { fill: lineColor },
          },
          {
            type: 'polygon',
            shape: {
              points: [
                [compareX, comparePoint[1]],
                [compareX - arrowSize, comparePoint[1] - arrowSize - 2],
                [compareX + arrowSize, comparePoint[1] - arrowSize - 2],
              ],
            },
            style: { fill: lineColor },
          },
          {
            type: 'rect',
            shape: {
              x: bubbleX - (bubbleWidth / 2),
              y: lineY - bubbleHeight - 6,
              width: bubbleWidth,
              height: bubbleHeight,
              r: bubbleHeight / 2,
            },
            style: {
              fill: bubbleFill,
              stroke: bubbleBorder,
              lineWidth: 1,
            },
          },
          {
            type: 'text',
            style: {
              text: label,
              x: bubbleX,
              y: lineY - 6 - (bubbleHeight / 2),
              fill: bubbleText,
              fontSize: 11,
              fontWeight: 700,
              align: 'center',
              verticalAlign: 'middle',
            },
          },
        ],
      });
    });

    return graphics;
  }

  private resolveSeriesIndex(selector: any, fallbackIndex: number): number {
    if (Number.isInteger(selector)) {
      const index = Number(selector);
      return index >= 0 && index < this.lastSeriesConfigs.length ? index : fallbackIndex;
    }

    const normalized = String(selector ?? '').trim().toLowerCase();
    if (!normalized) {
      return fallbackIndex;
    }

    const matchIndex = this.lastSeriesConfigs.findIndex((series, index) => {
      return (
        String(series.id).toLowerCase() === normalized ||
        String(series.name).toLowerCase() === normalized ||
        String(series.valueField).toLowerCase() === normalized ||
        String(index).toLowerCase() === normalized
      );
    });

    return matchIndex >= 0 ? matchIndex : fallbackIndex;
  }

  private scheduleResize(): void {
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
    }

    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = 0;
      this.myChart?.resize();
      this.updateComparisonGraphics();
    });
  }

  private resolveSeriesConfigs(): SeriesConfig[] {
    const config = this.getConfig();
    const palette = this.palette();
    const configuredSeries = Array.isArray(config.series)
      ? config.series
      : Array.isArray(this.data?.series)
        ? this.data.series
        : [];

    if (configuredSeries.length) {
      return configuredSeries.map((series: any, index: number) => ({
        id: String(series.id || series.key || series.valueField || `series-${index}`),
        name: String(series.name || series.label || series.valueField || `Series ${index + 1}`),
        valueField: String(series.valueField || series.key || ''),
        color: series.color || palette[index % palette.length],
        barWidth: series.barWidth,
        barMaxWidth: series.barMaxWidth,
        valuePrefix: series.valuePrefix ?? series.prefix,
        valueSuffix: series.valueSuffix ?? series.suffix,
        valueDecimals: series.valueDecimals ?? series.decimals,
      }));
    }

    const valueField = this.valueKey();
    if (!valueField) {
      return [];
    }

    return [
      {
        id: valueField,
        name: String(config.seriesName || this.data?.seriesName || 'Value'),
        valueField,
        color: palette[0],
      },
    ];
  }

  private buildStatusMatrix(rows: any[], categories: string[]): StatusMatrixRow[] {
    const configuredRows = this.getConfig().statusRows || this.getConfig().metricsRows || this.data?.statusRows || [];
    if (!Array.isArray(configuredRows) || !configuredRows.length) {
      return [];
    }

    return configuredRows.map((rowConfig: any) => ({
      label: String(rowConfig.label || rowConfig.name || rowConfig.valueKey || ''),
      valueKey: String(rowConfig.valueKey || rowConfig.key || ''),
      cells: categories.map((category, index) => {
        const row = rows[index];
        const value = rowConfig.value
          ? rowConfig.value
          : this.resolvePath(row, String(rowConfig.valueKey || rowConfig.key || ''));
        const label = rowConfig.labelKey
          ? this.resolvePath(row, String(rowConfig.labelKey))
          : value;
        return {
          category,
          value: String(value ?? ''),
          label: String(label ?? value ?? ''),
          source: row,
        };
      }),
    }));
  }

  private buildCategories(rows: any[]): string[] {
    return rows.map((row, index) => this.categoryLabel(row, index));
  }

  private sortRows(rows: any[], config: any): any[] {
    const sortConfig = config?.sort || {};
    const rawField = String(sortConfig.field || '').trim();
    const field = rawField || (sortConfig.byXAxis === true ? this.categoryKey() : '');
    if (!field) {
      return rows;
    }

    const direction = String(sortConfig.direction || 'asc').toLowerCase() === 'desc' ? -1 : 1;
    const type = String(sortConfig.type || 'natural').toLowerCase();
    const collator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });
    const sortableRows = sortConfig.preserveFirstRow === true && rows.length > 1
      ? rows.slice(1)
      : rows;
    const sortedRows = [...sortableRows].sort((left, right) => {
      const leftValue = this.resolvePath(left, field);
      const rightValue = this.resolvePath(right, field);
      if (type === 'number') {
        return ((Number(leftValue) || 0) - (Number(rightValue) || 0)) * direction;
      }

      return collator.compare(String(leftValue ?? ''), String(rightValue ?? '')) * direction;
    });

    const orderedRows = sortConfig.preserveFirstRow === true && rows.length > 1
      ? [rows[0], ...sortedRows]
      : sortedRows;

    if (sortConfig.prioritizeHighlightedRow !== false) {
      const highlightedIndex = orderedRows.findIndex((row) => row?.__patPlannerHighlighted === true);
      if (highlightedIndex > 0) {
        const nextRows = orderedRows.slice();
        const [highlightedRow] = nextRows.splice(highlightedIndex, 1);
        nextRows.unshift(highlightedRow);
        return nextRows;
      }
    }

    return orderedRows;
  }

  private limitRows(rows: any[]): any[] {
    const maxItems = Number(this.getConfig().maxItems ?? this.data?.maxItems ?? 0);
    if (!Number.isFinite(maxItems) || maxItems <= 0 || rows.length <= maxItems) {
      return rows;
    }

    return rows.slice(0, maxItems);
  }

  private visibleRows(rows: any[]): any[] {
    if (!this.hasPagedScroller) {
      return rows;
    }

    return rows.slice(this.pageStartIndex, this.pageStartIndex + this.maxVisibleGroups);
  }

  private clampPageStart(value: number, totalRows: number): number {
    const maxStart = Math.max(0, totalRows - this.maxVisibleGroups);
    const normalized = Number.isFinite(value) ? Math.floor(value) : 0;
    return Math.max(0, Math.min(normalized, maxStart));
  }

  private extractRows(payload: any): any[] {
    const dataKey = this.data?.dataKey || this.getConfig().dataKey;
    const responsePath = this.data?.responsePath || this.getConfig().responsePath;
    const source = responsePath ? this.resolvePath(payload, responsePath) : payload;
    const candidates = [
      dataKey ? this.resolvePath(source, dataKey) : undefined,
      dataKey ? this.resolvePath(payload, dataKey) : undefined,
      dataKey ? this.resolvePath(source, `data.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `data.0.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseData.0.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseList.0.data.0.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseList.0.data.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseList.0.responseData.0.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseList.0.responseData.${dataKey}`) : undefined,
      this.resolvePath(source, 'data.records'),
      this.resolvePath(source, 'records'),
      this.resolvePath(source, 'responseData.0'),
      this.resolvePath(source, 'responseData'),
      this.resolvePath(source, 'responseList.0.data.0'),
      this.resolvePath(source, 'responseList.0.data'),
      this.resolvePath(source, 'responseList.0.responseData.0'),
      this.resolvePath(source, 'responseList.0.responseData'),
      this.resolvePath(source, 'data.0'),
      this.resolvePath(source, 'data'),
      source,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }

      if (candidate && typeof candidate === 'object' && dataKey && Array.isArray(candidate[dataKey])) {
        return candidate[dataKey];
      }
    }

    return [];
  }

  private activeWidgetData(): any {
    const view = this.activeView();
    return {
      ...this.data,
      ...view,
      chartConfig: this.getConfig(),
    };
  }

  private activeView(): ViewTab {
    return this.viewTabs[Math.min(this.activeViewIndex, this.viewTabs.length - 1)] || this.viewTabs[0];
  }

  private getConfig(): any {
    return this.activeConfig || {};
  }

  private refreshViewState(): void {
    const rootConfig = this.data?.chartConfig || {};
    const views = rootConfig.views || this.data?.views;
    this.viewTabs = Array.isArray(views) && views.length > 0
      ? views.map((view: any, index: number) => ({
          id: String(view.id ?? view.key ?? index),
          label: String(view.label ?? view.name ?? view.title ?? `View ${index + 1}`),
          ...view,
        }))
      : [{ id: 'default', label: String(this.data?.defaultViewLabel || 'Default') }];

    const activeView = this.viewTabs[Math.min(this.activeViewIndex, this.viewTabs.length - 1)] || {};
    const { views: _views, ...baseConfig } = rootConfig;
    this.activeConfig = {
      ...baseConfig,
      ...(activeView.chartConfig || {}),
    };
  }

  private categoryLabel(row: any, index: number): string {
    return String(this.resolvePath(row, this.categoryKey()) ?? index + 1);
  }

  private categoryKey(): string {
    return String(this.getConfig().categoryKey || this.data?.categoryKey || this.data?.xAxis || 'category');
  }

  private valueKey(): string {
    return String(this.getConfig().valueKey || this.data?.valueKey || 'value');
  }

  private palette(): string[] {
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    return this.getConfig().colors || this.data?.colors || theme.chart?.palette || ['#374151', '#dc2626', '#22c55e'];
  }

  private formatTooltip(params: any): string {
    const rows = Array.isArray(params) ? params : [params];
    const title = rows[0]?.axisValueLabel || rows[0]?.name || '';
    const raw = rows[0]?.data?.raw;
    const seriesLines = rows
      .map((item: any) => {
        const series = this.lastSeriesConfigs.find((entry) => entry.name === item.seriesName);
        const rawValue = Array.isArray(item.value) ? item.value[1] : item.value;
        return `<div>${item.marker}<strong>${item.seriesName}</strong>: ${this.formatValue(
          Number(rawValue),
          Number(series?.valueDecimals ?? this.getConfig().valueDecimals ?? 0),
          String(series?.valuePrefix ?? this.getConfig().valuePrefix ?? ''),
          String(series?.valueSuffix ?? this.getConfig().valueSuffix ?? ''),
          series?.valueFormat ?? this.getConfig().tooltipValueFormat ?? this.getConfig().valueFormat,
        )}</div>`;
      })
      .join('');

    const statusLines = this.statusMatrix.length && raw
      ? this.statusMatrix
          .map((statusRow) => {
            const value = this.resolvePath(raw, statusRow.valueKey);
            return value === undefined || value === null || value === ''
              ? ''
              : `<div><span style="color:#6b7280">${statusRow.label}</span>: <strong>${value}</strong></div>`;
          })
          .filter(Boolean)
          .join('')
      : '';

    const extraLines = raw
      ? this.resolveTooltipFields()
          .map((field) => {
            const value = this.resolveTooltipFieldValue(raw, field);
            return value === undefined || value === null || value === ''
              ? ''
              : `<div><span style="color:#6b7280">${field.label}</span>: <strong>${value}</strong></div>`;
          })
          .filter(Boolean)
          .join('')
      : '';

    return `<div style="line-height:1.65;min-width:170px"><strong>${title}</strong>${seriesLines}${statusLines}${extraLines}</div>`;
  }

  private resolveTooltipFields(): Array<{ label: string; valueKey?: string; field?: string; fields?: string[] }> {
    const config = this.getConfig();
    const fields = config.tooltipFields || config.extraTooltipFields || config.tooltip?.fields || [];
    return Array.isArray(fields)
      ? fields
          .filter((field: any) => field && typeof field === 'object')
          .map((field: any) => ({
            label: String(field.label || field.title || field.valueKey || field.field || ''),
            valueKey: field.valueKey,
            field: field.field,
            fields: Array.isArray(field.fields) ? field.fields : undefined,
          }))
          .filter((field) => field.label)
      : [];
  }

  private resolveTooltipFieldValue(raw: any, field: { valueKey?: string; field?: string; fields?: string[] }): any {
    const keys = [
      field.valueKey,
      field.field,
      ...(Array.isArray(field.fields) ? field.fields : []),
    ].filter((key): key is string => typeof key === 'string' && key.trim() !== '');

    for (const key of keys) {
      const value = this.resolvePath(raw, key);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  private formatValue(value: number, decimals: number, prefix: string, suffix: string, format?: string): string {
    if (!Number.isFinite(value)) {
      return '-';
    }

    if (format === 'compact' || format === 'compact-short') {
      return `${prefix}${this.formatCompactValue(value, decimals)}${suffix}`;
    }

    return `${prefix}${value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}${suffix}`;
  }

  private formatCompactValue(value: number, decimals: number): string {
    const absValue = Math.abs(value);
    const units = [
      { threshold: 1_000_000_000, suffix: 'B' },
      { threshold: 1_000_000, suffix: 'M' },
      { threshold: 1_000, suffix: 'k' },
    ];
    const unit = units.find((entry) => absValue >= entry.threshold);

    if (!unit) {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }

    const scaled = value / unit.threshold;
    const resolvedDecimals = decimals > 0
      ? decimals
      : Math.abs(scaled) < 10 && !Number.isInteger(scaled)
        ? 1
        : 0;

    return `${scaled.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: resolvedDecimals,
    })}${unit.suffix}`;
  }

  private showNoData(): void {
    this.statusMatrix = [];
    this.myChart?.clear();
    this.myChart?.setOption({
      xAxis: { show: false, type: 'category', data: [] },
      yAxis: { show: false, type: 'value' },
      series: [],
      graphic: {
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
          text: this.data?.noDataText || 'No data available',
          fill: '#6b7280',
          fontSize: 14,
          fontWeight: 600,
        },
      },
    });
    this.myChart?.hideLoading();
  }

  private highlightCategories(categories: string[]): void {
    if (!this.myChart) {
      return;
    }

    const selectedCategories = new Set(categories.map((category) => String(category)));
    const option = this.myChart.getOption() as any;
    const updatedSeries = (option?.series || []).map((series: any) => ({
      ...series,
      data: Array.isArray(series.data)
        ? series.data.map((point: any, index: number) => {
            const active = selectedCategories.has(String(this.allCategories[index]));
            return {
              ...point,
              itemStyle: {
                ...(point?.itemStyle || {}),
                opacity: active ? 1 : 0.16,
              },
              label: {
                ...(point?.label || {}),
                opacity: active ? 1 : 0.16,
              },
            };
          })
        : series.data,
    }));

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  private resetCategoryHighlight(): void {
    if (!this.myChart) {
      return;
    }

    const option = this.myChart.getOption() as any;
    const updatedSeries = (option?.series || []).map((series: any) => ({
      ...series,
      data: Array.isArray(series.data)
        ? series.data.map((point: any) => ({
            ...point,
            itemStyle: {
              ...(point?.itemStyle || {}),
              opacity: 1,
            },
            label: {
              ...(point?.label || {}),
              opacity: 1,
            },
          }))
        : series.data,
    }));

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  private selectLegendsByIndices(indices: number[]): void {
    if (!this.myChart) {
      return;
    }

    const selectedIndices = new Set(indices);
    const selected = this.allSeriesNames.reduce<Record<string, boolean>>((acc, seriesName, index) => {
      acc[seriesName] = indices.length === 0 || selectedIndices.has(index);
      return acc;
    }, {});
    this.myChart.setOption({ legend: { selected } }, false);
  }

  private normalizeStatus(status: string): string {
    return String(status || '').trim().toLowerCase().replace(/\s+/g, '-');
  }

  private resolvePath(source: any, path: string): any {
    if (!source || !path) {
      return undefined;
    }
    return String(path)
      .split('.')
      .reduce((current: any, key: string) => current?.[key], source);
  }
}
